import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, Users, Home as HomeIcon, ExternalLink, Check, X, Save, Loader2, Plus, BarChart3 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { api, friendlyError } from '../lib/api';
import { FormBanner, FormError } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';
import ImageUploader from '../components/profile/ImageUploader';
import TagsField from '../components/profile/TagsField';
import CompletenessCard from '../components/profile/CompletenessCard';
import { KpiGrid, SeriesChart } from '../components/profile/MetricsDashboard';
import ActivityFeed from '../components/profile/ActivityFeed';

const TABS = [
  { id: 'overview', label: 'Resumen' },
  { id: 'info', label: 'Información' },
  { id: 'team', label: 'Equipo' },
];

export default function AgencyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agencyId, setAgencyId] = useState(user?.agency_id || null);
  const [tab, setTab] = useState('overview');
  const [dashboard, setDashboard] = useState(null);
  const [members, setMembers] = useState({ active: [], pending: [] });
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [createForm, setCreateForm] = useState({ name: '', city: '', phone: '' });
  const v = useFormValidation();

  useEffect(() => {
    if (!agencyId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([api.getAgencyDashboard(agencyId), api.listAgencyMembers(agencyId)])
      .then(([d, m]) => {
        if (!d?.agency) {
          // Stale agency_id on user — fall back to onboarding flow
          setAgencyId(null);
          return;
        }
        setDashboard(d); setMembers(m);
      })
      .catch((e) => {
        const msg = friendlyError(e);
        toast({ title: 'Error', description: msg, variant: 'destructive' });
        // 404 → stale reference; fall back to onboarding so the UI doesn't crash
        if (/no encontrada|not found|404/i.test(msg)) setAgencyId(null);
      })
      .finally(() => setLoading(false));
  }, [agencyId, toast]);

  // ── Onboarding: not yet in agency ──
  useEffect(() => {
    if (!searchQ.trim()) { setSearchResults([]); return; }
    const t = setTimeout(() => api.agencySearch(searchQ).then(r => setSearchResults(r.items)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [searchQ]);

  const createAgency = async () => {
    v.reset();
    const ok = v.validate({
      name: !createForm.name || createForm.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
    });
    if (!ok) return;
    setCreating(true);
    try {
      const a = await api.createAgency(createForm);
      toast({ title: '¡Inmobiliaria creada!', description: 'Solicitarás verificación al equipo de Habita.' });
      setAgencyId(a.id);
    } catch (e) { v.applyError(e, 'No pudimos crear la inmobiliaria.'); }
    finally { setCreating(false); }
  };

  const requestJoin = async (a) => {
    try {
      await api.requestAgencyJoin(a.id);
      toast({ title: 'Solicitud enviada', description: `${a.name} revisará tu solicitud.` });
    } catch (e) {
      toast({ title: 'No pudimos enviar la solicitud', description: friendlyError(e), variant: 'destructive' });
    }
  };

  const approve = async (uid) => {
    try { await api.approveMember(agencyId, uid); const m = await api.listAgencyMembers(agencyId); setMembers(m); toast({ title: 'Solicitud aprobada' }); }
    catch (e) { toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' }); }
  };
  const reject = async (uid) => {
    try { await api.rejectMember(agencyId, uid); const m = await api.listAgencyMembers(agencyId); setMembers(m); toast({ title: 'Solicitud rechazada' }); }
    catch (e) { toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' }); }
  };

  if (loading) return (<div className="min-h-screen bg-habita-cream"><Header /><div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 text-habita animate-spin" /></div></div>);

  // ─── No agency yet → onboarding view ───
  if (!agencyId) {
    return (
      <div className="min-h-screen bg-habita-cream">
        <SEO title="Inmobiliaria · habita.co" noindex />
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <h1 className="font-serif text-3xl font-bold text-habita-dark">Tu inmobiliaria en habita.co</h1>
          <p className="text-gray-600 mt-2">Conecta tu cuenta a una inmobiliaria existente o crea una nueva. Solo puedes pertenecer a una a la vez.</p>

          {/* Search existing */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-6" data-testid="agency-search-section">
            <h2 className="font-serif text-lg font-bold text-habita-dark">Buscar inmobiliaria existente</h2>
            <Input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Nombre de la inmobiliaria..." className="mt-3 h-11" data-testid="agency-search-input" />
            {searchResults.length > 0 && (
              <div className="mt-3 space-y-2">
                {searchResults.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-habita-cream" data-testid={`agency-search-result-${a.id}`}>
                    <div className="flex items-center gap-3">
                      {a.logo_url ? <img src={a.logo_url} alt="" className="w-10 h-10 rounded-lg object-cover" /> : <div className="w-10 h-10 rounded-lg bg-habita-mint flex items-center justify-center text-habita font-bold">{a.name.charAt(0)}</div>}
                      <div><p className="font-semibold text-sm">{a.name}</p><p className="text-xs text-gray-500">{a.city}</p></div>
                    </div>
                    <Button size="sm" onClick={() => requestJoin(a)} className="btn-habita rounded-full" data-testid={`agency-request-join-${a.id}`}>Solicitar unirse</Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create new */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mt-4" data-testid="agency-create-section">
            <h2 className="font-serif text-lg font-bold text-habita-dark">Crear una nueva inmobiliaria</h2>
            <FormBanner msg={v.banner} testid="agency-create-banner" />
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Nombre comercial</Label>
                <Input value={createForm.name} onChange={e => { setCreateForm(f => ({ ...f, name: e.target.value })); v.clearField('name'); }} className={`mt-1.5 h-11 ${v.errorClass('name')}`} placeholder="Inmobiliaria Norte" data-testid="agency-name-input" {...v.field('name')} />
                <FormError msg={v.fieldErrors.name} testid="agency-error-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad principal</Label>
                <Input value={createForm.city} onChange={e => setCreateForm(f => ({ ...f, city: e.target.value }))} className="mt-1.5 h-11" placeholder="Bogotá" data-testid="agency-city-input" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Teléfono</Label>
                <Input value={createForm.phone} onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))} className="mt-1.5 h-11" placeholder="+57 300 000 0000" data-testid="agency-phone-input" />
              </div>
            </div>
            <Button onClick={createAgency} disabled={creating} className="btn-habita mt-4 h-11 px-6 rounded-full" data-testid="agency-create-submit-btn">
              {creating ? 'Creando...' : 'Crear inmobiliaria'}
            </Button>
          </div>

          <p className="text-sm text-center text-gray-500 mt-6">
            <Link to="/mi-cuenta" className="text-habita font-semibold hover:underline">Continuar como agente independiente →</Link>
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const a = dashboard?.agency;
  const kpis = dashboard?.kpis || {};

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO title={`${a?.name} · Dashboard`} noindex />
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            {a?.logo_url ? <img src={a.logo_url} alt="" className="w-12 h-12 rounded-xl object-cover" /> : <div className="w-12 h-12 rounded-xl bg-habita flex items-center justify-center"><Building2 className="w-6 h-6 text-white" /></div>}
            <div>
              <h1 className="font-serif text-2xl font-bold text-habita-dark">{a?.name}</h1>
              <Link to={`/inmobiliaria/${a?.slug}`} className="text-xs text-habita hover:underline flex items-center gap-1" target="_blank" data-testid="agency-view-public-link"><ExternalLink className="w-3 h-3" /> Ver perfil público</Link>
            </div>
          </div>
        </div>

        <div className="border-b border-gray-200 mb-6 flex gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`dashboard-tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${tab === t.id ? 'border-habita text-habita' : 'border-transparent text-gray-600'}`}>{t.label}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiCard icon={HomeIcon} label="Propiedades activas" value={kpis.properties_active} testid="kpi-properties-active" />
              <KpiCard icon={HomeIcon} label="En revisión" value={kpis.properties_pending} testid="kpi-properties-pending" />
              <KpiCard icon={Users} label="Asesores activos" value={kpis.team_active} testid="kpi-team-active" />
              <KpiCard icon={Users} label="Solicitudes pendientes" value={kpis.team_pending} accent="amber" testid="kpi-team-pending" />
            </div>
            <AgencyMetricsSection agencyId={a.id} />
          </div>
        )}

        {tab === 'info' && <AgencyEditForm agency={a} onSaved={(updated) => setDashboard(d => ({ ...d, agency: updated }))} />}

        {tab === 'team' && (
          <div className="space-y-6">
            {members.pending.length > 0 && (
              <div data-testid="pending-members-section">
                <h3 className="font-serif text-lg font-bold text-habita-dark mb-3">Solicitudes pendientes ({members.pending.length})</h3>
                <div className="space-y-2">
                  {members.pending.map(m => (
                    <div key={m.id} className="bg-white rounded-xl border border-amber-200 p-4 flex items-center justify-between gap-3" data-testid={`pending-member-${m.user_id}`}>
                      <div className="flex items-center gap-3">
                        <img src={m.user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.user.name)}&background=00824D&color=fff`} alt="" className="w-10 h-10 rounded-full" />
                        <div><p className="font-semibold text-sm">{m.user.name}</p><p className="text-xs text-gray-500">{m.user.email}</p></div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => reject(m.user_id)} data-testid={`reject-${m.user_id}`}><X className="w-3 h-3 mr-1" /> Rechazar</Button>
                        <Button size="sm" onClick={() => approve(m.user_id)} className="btn-habita" data-testid={`approve-${m.user_id}`}><Check className="w-3 h-3 mr-1" /> Aprobar</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="font-serif text-lg font-bold text-habita-dark mb-3">Asesores activos ({members.active.length})</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {members.active.map(m => (
                  <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3" data-testid={`active-member-${m.user_id}`}>
                    <img src={m.user.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.user.name)}&background=00824D&color=fff`} alt="" className="w-12 h-12 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm truncate">{m.user.name}</p>
                      <p className="text-xs text-gray-500">{m.title || (m.role === 'owner' ? 'Director' : 'Asesor')} · {m.active_properties} prop.</p>
                    </div>
                    {m.role !== 'owner' && <button onClick={() => api.removeMember(agencyId, m.user_id).then(() => api.listAgencyMembers(agencyId).then(setMembers))} className="text-xs text-rose-600 hover:underline" data-testid={`remove-${m.user_id}`}>Quitar</button>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
        <Icon className="w-4 h-4 text-gray-400" />
      </div>
      <p className={`text-3xl font-bold ${accent === 'amber' && value > 0 ? 'text-amber-600' : 'text-habita-dark'}`}>{value ?? 0}</p>
    </div>
  );
}

function AgencyEditForm({ agency, onSaved }) {
  const { toast } = useToast();
  const v = useFormValidation();
  const a = agency || {};
  const [form, setForm] = useState({
    name: a.name || '', description: a.description || '', history: a.history || '',
    city: a.city || '', phone: a.phone || '', whatsapp: a.whatsapp || '',
    email: a.email || '', website: a.website || '', founded_year: a.founded_year || '',
    agents_count_approx: a.agents_count_approx || '',
    logo_url: a.logo_url || '', cover_url: a.cover_url || '',
    gallery: a.gallery || [], video_url: a.video_url || '',
    specialties: a.specialties || [], cities_operating: a.cities_operating || [],
    social: a.social || {}, hours: a.hours || {},
  });
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);  // 'logo' | 'cover' | 'gallery'
  const set = (k, val) => { setForm(f => ({ ...f, [k]: val })); v.clearField(k); };

  if (!agency?.id) {
    return <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-gray-500" data-testid="agency-info-empty">Cargando información de la inmobiliaria…</div>;
  }

  const upload = async (file, kind) => {
    if (!file) return;
    setUploadingKind(kind);
    try {
      const res = await api.uploadImage(file);
      if (kind === 'logo') set('logo_url', res.secure_url || res.url);
      else if (kind === 'cover') set('cover_url', res.secure_url || res.url);
      else if (kind === 'gallery') setForm(f => ({ ...f, gallery: [...(f.gallery || []), res.secure_url || res.url].slice(0, 10) }));
    } catch (e) {
      toast({ title: 'No pudimos subir la imagen', description: friendlyError(e), variant: 'destructive' });
    } finally { setUploadingKind(null); }
  };

  const onFileChange = (kind) => (e) => {
    const files = Array.from(e.target.files || []);
    if (kind === 'gallery') files.forEach(f => upload(f, 'gallery'));
    else if (files[0]) upload(files[0], kind);
    e.target.value = '';
  };

  const removeGalleryAt = (i) => setForm(f => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      ['founded_year', 'agents_count_approx'].forEach(k => {
        if (payload[k]) payload[k] = parseInt(payload[k]);
        else delete payload[k];
      });
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
      });
      const updated = await api.updateAgency(agency.id, payload);
      onSaved(updated);
      toast({ title: 'Cambios guardados', description: `Perfil ${updated.completeness?.pct || 0}% completo` });
    } catch (e) { v.applyError(e, 'No pudimos guardar los cambios.'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="agency-info-form">
      <CompletenessCard completeness={agency.completeness} />

      <FormBanner msg={v.banner} testid="agency-info-banner" />

      {/* Branding uploads */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-serif text-lg font-bold text-habita-dark mb-4">Branding</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <ImageUploader label="Logo (cuadrado)" current={form.logo_url} uploading={uploadingKind === 'logo'} onFile={onFileChange('logo')} onClear={() => set('logo_url', '')} testid="logo-uploader" aspect="aspect-square" />
          <ImageUploader label="Portada (banner)" current={form.cover_url} uploading={uploadingKind === 'cover'} onFile={onFileChange('cover')} onClear={() => set('cover_url', '')} testid="cover-uploader" aspect="aspect-[16/6]" />
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="gallery-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg font-bold text-habita-dark">Galería institucional ({form.gallery.length}/10)</h3>
          <label className={`text-xs font-semibold px-3 h-9 rounded-full bg-habita-mint text-habita cursor-pointer flex items-center gap-1 ${form.gallery.length >= 10 ? 'opacity-50 pointer-events-none' : ''}`} data-testid="gallery-add-btn">
            <Plus className="w-3 h-3" /> Agregar foto
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFileChange('gallery')} />
          </label>
        </div>
        {form.gallery.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Sube fotos de tu oficina, equipo o fachada (recomendado 5-10).</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
            {form.gallery.map((url, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-habita-cream group" data-testid={`gallery-item-${i}`}>
                <img src={url} alt={`Galería ${i + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`gallery-remove-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {uploadingKind === 'gallery' && <div className="aspect-square rounded-lg bg-habita-cream flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-habita" /></div>}
          </div>
        )}
      </div>

      {/* Video */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-serif text-lg font-bold text-habita-dark mb-3">Video institucional</h3>
        <Label className="text-xs uppercase tracking-widest text-gray-500">URL (YouTube, Vimeo o link MP4)</Label>
        <Input value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="mt-1.5 h-11" data-testid="agency-edit-video_url" />
        <p className="text-xs text-gray-500 mt-1.5">Próximamente: subida directa de video.</p>
      </div>

      {/* Corporate info */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-serif text-lg font-bold text-habita-dark">Información corporativa</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Nombre comercial" k="name" form={form} set={set} v={v} />
          <TextField label="Año de fundación" k="founded_year" type="number" form={form} set={set} v={v} />
          <TextField label="Ciudad principal" k="city" form={form} set={set} v={v} />
          <TextField label="N° aprox. asesores" k="agents_count_approx" type="number" form={form} set={set} v={v} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Descripción breve</Label>
          <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita" data-testid="agency-edit-description" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Historia de la empresa</Label>
          <textarea value={form.history} onChange={e => set('history', e.target.value)} rows={5} placeholder="Cuéntale a tus clientes el origen de tu inmobiliaria, hitos importantes, valores..." className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita" data-testid="agency-edit-history" />
        </div>
        <TagsField label="Especialidades" testid="agency-tags-specialties" value={form.specialties} onChange={val => set('specialties', val)} placeholder="Ej: Apartamentos, Oficinas, Fincas..." />
        <TagsField label="Ciudades donde operas" testid="agency-tags-cities_operating" value={form.cities_operating} onChange={val => set('cities_operating', val)} placeholder="Bogotá, Medellín, Cartagena..." />
      </div>

      {/* Contact */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-serif text-lg font-bold text-habita-dark">Contacto y redes</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="WhatsApp" k="whatsapp" form={form} set={set} v={v} placeholder="+57 300 000 0000" />
          <TextField label="Teléfono" k="phone" form={form} set={set} v={v} />
          <TextField label="Email corporativo" k="email" form={form} set={set} v={v} />
          <TextField label="Sitio web" k="website" form={form} set={set} v={v} placeholder="https://..." />
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
            {['instagram', 'facebook', 'linkedin', 'tiktok'].map(s => (
              <div key={s}>
                <Label className="text-xs uppercase tracking-widest text-gray-500 capitalize">{s}</Label>
                <Input value={form.social[s] || ''} onChange={e => set('social', { ...form.social, [s]: e.target.value })} placeholder={`https://${s}.com/...`} className="mt-1.5 h-11" data-testid={`agency-social-${s}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      <Button onClick={save} disabled={saving || !!uploadingKind} className="btn-habita rounded-full px-8 h-12 w-full sm:w-auto" data-testid="agency-save-btn">
        <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando...' : 'Guardar cambios'}
      </Button>
    </div>
  );
}

function TextField({ label, k, type = 'text', form, set, v, placeholder }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
      <Input type={type} value={form[k]} onChange={e => set(k, e.target.value)} placeholder={placeholder} className={`mt-1.5 h-11 ${v.errorClass(k)}`} data-testid={`agency-edit-${k}`} {...v.field(k)} />
      <FormError msg={v.fieldErrors[k]} />
    </div>
  );
}

function AgencyMetricsSection({ agencyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.getAgencyMetrics(agencyId, windowDays)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [agencyId, windowDays]);

  return (
    <div className="space-y-5" data-testid="agency-metrics-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-serif text-lg font-bold text-habita-dark">Inteligencia comercial</h3>
        <div className="flex gap-1 rounded-full bg-white border border-gray-200 p-1" data-testid="agency-metrics-window-switch">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-3 h-8 text-xs font-semibold rounded-full transition-colors ${windowDays === d ? 'bg-habita text-white' : 'text-gray-600 hover:text-habita-dark'}`}
              data-testid={`agency-metrics-window-${d}`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      {!loading && data && (
        <>
          <KpiGrid
            kpis={data.kpis}
            order={['agency_views', 'property_views', 'whatsapp_clicks', 'phone_clicks', 'contacts', 'visit_requests', 'leads']}
            testid="agency-kpis"
          />
          <SeriesChart series={data.series} testid="agency-series-chart" />

          <div className="grid lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="agency-top-properties">
              <p className="font-serif font-bold text-habita-dark mb-3">Propiedades más vistas</p>
              {data.top_properties?.length === 0
                ? <p className="text-sm text-gray-500 py-6 text-center">Sin datos aún en este período.</p>
                : (
                  <ul className="space-y-2.5">
                    {data.top_properties.map(p => (
                      <li key={p.id} className="flex items-center gap-3">
                        {p.images?.[0]
                          ? <img src={p.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />
                          : <div className="w-12 h-12 rounded-lg bg-habita-mint" />}
                        <div className="flex-1 min-w-0">
                          <Link to={`/propiedad/${p.id}`} className="text-sm font-semibold text-habita-dark hover:text-habita truncate block">{p.title || `${p.city}`}</Link>
                          <p className="text-xs text-gray-500">{p.city}</p>
                        </div>
                        <p className="text-sm font-bold text-habita tabular-nums">{p.views}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="agency-top-agents">
              <p className="font-serif font-bold text-habita-dark mb-3">Asesores más activos</p>
              {data.top_agents?.length === 0
                ? <p className="text-sm text-gray-500 py-6 text-center">Sin datos aún en este período.</p>
                : (
                  <ul className="space-y-2.5">
                    {data.top_agents.map(u => (
                      <li key={u.id} className="flex items-center gap-3">
                        {u.avatar
                          ? <img src={u.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
                          : <div className="w-10 h-10 rounded-full bg-habita-mint flex items-center justify-center text-habita font-bold">{u.name?.[0]}</div>}
                        <div className="flex-1 min-w-0">
                          <Link to={u.slug ? `/agente/${u.slug}` : '#'} className="text-sm font-semibold text-habita-dark hover:text-habita truncate block">{u.name}</Link>
                          {u.title && <p className="text-xs text-gray-500 truncate">{u.title}</p>}
                        </div>
                        <p className="text-sm font-bold text-habita tabular-nums">{u.activity}</p>
                      </li>
                    ))}
                  </ul>
                )}
            </div>
          </div>

          <ActivityFeed scope="agency" agencyId={agencyId} title="Actividad de la inmobiliaria" limit={20} testid="agency-activity-feed" />
        </>
      )}
    </div>
  );
}

